import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { IUser } from '@/features/user/store/user.model';

import { selectProfileWalletsByUserId } from '@/store/selectors';
import { createWallet, setCurrentWallet } from '@/features/wallets/store/actions/wallet.actions';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { useWalletForm } from './useWalletForm';

export function useAddWallet(user: IUser, onClose?: () => void) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isLoading = useAppSelector((state) => selectProfileWalletsByUserId(state, user.userId)?.create?.isLoading);

  const {
    watch,
    setValue,
    getValues,
    walletTypeOptions,
    bicOptions,
    isWalletTypeEditable,
    isBicEditable,
    canAddWallet,
  } = useWalletForm({
    user,
    defaultValues: {},
  });

  const watchWalletTypeId = watch('walletTypeId');
  const watchBic = watch('bic');
  const watchEventName = watch('eventName');

  const shouldDisabledAddWallet = user?.userStatus === 'PENDING' && Number(user?.codeStatus) === 110012;

  const isFormDisabled = useMemo(
    () => !watchWalletTypeId || !watchBic || !watchEventName,
    [watchBic, watchEventName, watchWalletTypeId],
  );

  const onCreateWallet = useCallback(() => {
    const value = getValues();
    dispatch(
      createWallet(value, user.userId, (walletId: number) => {
        navigate(DashboardHelpers.generateProfilePath(user.userId, { walletId }));
        dispatch(setCurrentWallet(user.userId, walletId));
        onClose();
      }),
    );
  }, [dispatch, getValues, navigate, onClose, user.userId]);

  return {
    shouldDisabledAddWallet,
    walletTypeOptions,
    bicOptions,
    isFormDisabled,
    isLoading,
    watch,
    setValue,
    onCreateWallet,
    isWalletTypeEditable,
    isBicEditable,
    canAddWallet,
  };
}
